import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SideDrawer from '../../../SideDrawer/SideDrawer';
import MultiSelectSearchDropdown from '../../../../ManageDealDrawer/Components/MultiSelectSearchDropdown/MultiSelectSearchDropdown';
import Button from '../../../Button/Button';
import { ColumnDragList } from './components';
import { DataTableColumn, DataTableColumnType, EXCLUDED_COLUMN_TYPES } from '../../DataTable.types';
import {
    closeColumnCustomizationDrawer,
    updateTempSelectedColumns,
    applyColumnCustomization,
    resetColumnCustomization,
} from '../../../../../store/investor/action';
import {
    selectColumnCustomizationOpen,
    selectTempSelectedColumns,
    selectSelectedColumns,
    selectHasUnsavedColumnChanges,
} from '../../../../../store/investor/selectors';
import { configToIds, idsToConfig, validateColumnIds, getDefaultColumnIds } from '../../utils/columnTransformUtils';
import styles from './DataTableColumnCustomizationDrawer.module.scss';
import { filterColumnToMap } from '../../../../../utils/utils';

export interface DataTableColumnCustomizationDrawerProps {
    masterConfig: DataTableColumn[];
    onApply: (selectedColumns: DataTableColumn[]) => void;
    pinnedColumns?: { field: string; position: 'left' | 'right' }[];
    width?: string;
    title?: string;
}

const DataTableColumnCustomizationDrawer: React.FC<DataTableColumnCustomizationDrawerProps> = ({
    masterConfig,
    onApply,
    pinnedColumns = [],
    width = '40%',
    title = 'Edit Columns',
}) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);

    // Redux state selectors
    const open = useSelector(selectColumnCustomizationOpen);
    const tempSelectedColumns = useSelector(selectTempSelectedColumns);
    const selectedColumns = useSelector(selectSelectedColumns);
    const hasUnsavedChanges = useSelector(selectHasUnsavedColumnChanges);

    // Create pinned columns set for quick lookup
    const pinnedColumnsSet = useMemo(() => new Set(pinnedColumns.map((pc) => pc.field)), [pinnedColumns]);

    // Memoized column metadata map for efficient lookup
    // Prioritize visible columns (show === undefined || show === true) over hidden ones
    const columnMetadataMap = useMemo(() => {
        const map = new Map<string, DataTableColumn>();
        masterConfig.forEach((column) => {
            if (column && column.field) {
                filterColumnToMap(map, column);
            }
        });
        return map;
    }, [masterConfig]);

    // Convert columns to dropdown options, filtering out pinned columns and action/expandable/component columns
    const columnOptions = useMemo(
        () =>
            masterConfig
                .filter((col) => !pinnedColumnsSet.has(col.field)) // Exclude pinned columns
                .filter((col) => !EXCLUDED_COLUMN_TYPES.includes(col.type)) // Exclude action, expandable, and component columns
                .filter((col) => col.show !== false) // Exclude columns with show: false
                .map((col) => {
                    const label: string = typeof col.headerLabel === 'string' ? col.headerLabel : col.headerOriginalLabel;
                    return {
                        label,
                        value: col.field,
                    };
                }),
        [masterConfig, pinnedColumnsSet],
    );

    // Filter tempSelectedColumns to exclude pinned columns and action/expandable/component columns for selection
    const selectableColumns = useMemo(
        () =>
            tempSelectedColumns.filter((colId) => {
                if (pinnedColumnsSet.has(colId)) return false;
                const column = columnMetadataMap.get(colId);
                return column && !EXCLUDED_COLUMN_TYPES.includes(column.type);
            }),
        [tempSelectedColumns, pinnedColumnsSet, columnMetadataMap],
    );

    // Filter tempSelectedColumns to exclude action/expandable/component columns for drag list display
    const dragListColumns = useMemo(
        () =>
            tempSelectedColumns.filter((colId) => {
                const column = columnMetadataMap.get(colId);
                return column && !EXCLUDED_COLUMN_TYPES.includes(column.type);
            }),
        [tempSelectedColumns, columnMetadataMap],
    );

    // Memoized validation of temp selected columns
    const validatedTempColumns = useMemo(() => {
        return validateColumnIds(tempSelectedColumns, masterConfig);
    }, [tempSelectedColumns, masterConfig]);

    // Handle drawer close
    const handleClose = useCallback(() => {
        dispatch(closeColumnCustomizationDrawer());
    }, [dispatch]);

    // Handle column selection from dropdown - merge with pinned columns
    const handleColumnSelection = useCallback(
        (selectedValues: (string | null)[]) => {
            // Filter out null values first
            const validSelectedValues = selectedValues.filter((id): id is string => id !== null);

            // Separate pinned columns by position
            const leftPinnedColumns = pinnedColumns.filter((pc) => pc.position === 'left').map((pc) => pc.field);
            const rightPinnedColumns = pinnedColumns.filter((pc) => pc.position === 'right').map((pc) => pc.field);

            // Remove pinned columns and action/expandable columns from selectedValues to avoid duplicates
            const pinnedColumnIds = new Set([...leftPinnedColumns, ...rightPinnedColumns]);
            const nonPinnedSelected = validSelectedValues.filter((id) => {
                if (pinnedColumnIds.has(id)) return false;
                const column = columnMetadataMap.get(id);
                return column && !EXCLUDED_COLUMN_TYPES.includes(column.type);
            });

            // Build final array with proper ordering:
            // 1. Left pinned columns (at start)
            // 2. Non-pinned selected columns
            // 3. Right pinned columns (at end)
            const allSelected = [...leftPinnedColumns, ...nonPinnedSelected, ...rightPinnedColumns];

            dispatch(updateTempSelectedColumns({ tempSelectedColumns: allSelected }));
        },
        [dispatch, pinnedColumns, columnMetadataMap],
    );

    // Handle move column to top
    const handleMoveToTop = useCallback(
        (columnId: string) => {
            const currentColumns = [...tempSelectedColumns];
            const columnIndex = currentColumns.indexOf(columnId);

            // Find the actual count of pinned columns in the current array
            const leftPinnedColumns = pinnedColumns.filter((pc) => pc.position === 'left').map((pc) => pc.field);
            const rightPinnedColumns = pinnedColumns.filter((pc) => pc.position === 'right').map((pc) => pc.field);
            const allPinnedColumns = [...leftPinnedColumns, ...rightPinnedColumns];

            // Count how many pinned columns are actually at the beginning of the array
            let pinnedCount = 0;
            for (let i = 0; i < currentColumns.length; i++) {
                if (allPinnedColumns.includes(currentColumns[i])) {
                    pinnedCount++;
                } else {
                    break;
                }
            }

            if (columnIndex > pinnedCount) {
                // Remove the column from its current position
                const columnToMove = currentColumns.splice(columnIndex, 1)[0];
                // Insert it right after pinned columns
                currentColumns.splice(pinnedCount, 0, columnToMove);
                dispatch(updateTempSelectedColumns({ tempSelectedColumns: currentColumns }));
            }
        },
        [dispatch, tempSelectedColumns, pinnedColumns],
    );

    // Handle remove column
    const handleRemoveColumn = useCallback(
        (columnId: string) => {
            const currentColumns = tempSelectedColumns.filter((id) => id !== columnId);
            dispatch(updateTempSelectedColumns({ tempSelectedColumns: currentColumns }));
        },
        [dispatch, tempSelectedColumns],
    );

    // Handle column reorder from drag list
    const handleColumnReorder = useCallback(
        (reorderedColumns: string[]) => {
            // Get action, expandable, and component columns that should remain in their original positions
            const actionExpandableColumns = tempSelectedColumns.filter((colId) => {
                const column = columnMetadataMap.get(colId);
                return column && EXCLUDED_COLUMN_TYPES.includes(column.type);
            });

            // Merge reordered columns with action/expandable columns maintaining their positions
            const finalColumns = [...tempSelectedColumns];
            let reorderedIndex = 0;

            for (let i = 0; i < finalColumns.length; i++) {
                const colId = finalColumns[i];
                const column = columnMetadataMap.get(colId);

                // If it's not an action/expandable/component column, replace with reordered column
                if (column && !EXCLUDED_COLUMN_TYPES.includes(column.type)) {
                    if (reorderedIndex < reorderedColumns.length) {
                        finalColumns[i] = reorderedColumns[reorderedIndex];
                        reorderedIndex++;
                    }
                }
            }

            dispatch(updateTempSelectedColumns({ tempSelectedColumns: finalColumns }));
        },
        [dispatch, tempSelectedColumns, columnMetadataMap],
    );

    // Handle apply changes
    const handleApply = useCallback(async () => {
        try {
            setLoading(true);

            // Validate the selected columns
            const validatedColumns = validateColumnIds(tempSelectedColumns, masterConfig);

            if (validatedColumns.length === 0) {
                alert('Please select at least one column.');
                return;
            }

            // Apply changes to Redux state
            dispatch(applyColumnCustomization());

            // Convert column IDs back to configurations for the parent component
            const selectedColumnConfigs = idsToConfig(validatedColumns, masterConfig);

            // Notify parent component of the change
            onApply(selectedColumnConfigs);

            // Mark view as modified to show Save/Update buttons
            if ((window as any).markViewAsModified) {
                (window as any).markViewAsModified();
            }

            // Close the drawer
            dispatch(closeColumnCustomizationDrawer());
        } catch (error) {
            console.error('Error applying column customization:', error);
            alert('An error occurred while applying column changes. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [dispatch, tempSelectedColumns, masterConfig, onApply]);

    // Handle reset to default
    const handleReset = useCallback(() => {
        const confirmReset = window.confirm('This will reset the columns to the default configuration. Are you sure?');

        if (confirmReset) {
            const defaultColumnIds = getDefaultColumnIds(masterConfig);
            dispatch(resetColumnCustomization({ defaultColumns: defaultColumnIds }));
        }
    }, [dispatch, masterConfig]);

    // Handle reset to previous state (what was applied before opening drawer)
    const handleResetToPrevious = useCallback(() => {
        // Reset tempSelectedColumns back to selectedColumns (the previously applied state)
        dispatch(updateTempSelectedColumns({ tempSelectedColumns: selectedColumns }));
    }, [dispatch, selectedColumns]);

    // Don't render if not open
    if (!open) {
        return null;
    }

    return (
        <SideDrawer
            width={width}
            onClose={handleClose}
            heading={<span style={{ fontFamily: 'Gilroy-SemiBold', fontSize: '16px', color: '#000000' }}>{title}</span>}
            showHeading={true}
            allowClose={true}
        >
            <div className={styles.DrawerContainer}>
                <div className={styles.DrawerContent}>
                    <div className={styles.Section}>
                        <div className={styles.SectionHeader}>
                            <h3 className={styles.SectionTitle}>Select Columns</h3>
                            {hasUnsavedChanges && (
                                <button className={styles.ResetButton} onClick={handleResetToPrevious} type="button">
                                    Reset
                                </button>
                            )}
                        </div>

                        <MultiSelectSearchDropdown
                            options={columnOptions}
                            selectedItems={selectableColumns}
                            toggleSelection={handleColumnSelection}
                            placeholder="Select columns to display"
                            showSelectAll={true}
                        />
                    </div>
                    <div className={styles.Separator}></div>

                    {dragListColumns.length > 0 && (
                        <div className={styles.Section}>
                            <div>
                                <h3 className={styles.SectionTitle}>Selected Columns</h3>
                                <p className={styles.SectionSubtitle}>Drag to reorder columns</p>
                            </div>

                            <ColumnDragList
                                columns={dragListColumns}
                                columnMetadataMap={columnMetadataMap}
                                pinnedColumns={pinnedColumns}
                                onMoveToTop={handleMoveToTop}
                                onRemove={handleRemoveColumn}
                                onReorder={handleColumnReorder}
                            />
                        </div>
                    )}
                </div>

                <div className={styles.DrawerFooter}>
                    <Button
                        text="Cancel"
                        onClick={handleClose}
                        variant="secondary"
                        size="normal"
                        disabled={loading}
                        style={{ margin: 0 }}
                    />
                    <Button
                        text={loading ? 'Applying...' : 'Apply Changes'}
                        onClick={handleApply}
                        variant="primary"
                        size="normal"
                        disabled={loading || tempSelectedColumns.length === 0}
                        loading={loading}
                        style={{ margin: 0 }}
                    />
                </div>
            </div>
        </SideDrawer>
    );
};

export default DataTableColumnCustomizationDrawer;
